import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

function slugifyHeading(text) {
  return text
    .replace(/\*\*/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

function renderInline(text, keyPrefix = 'inline') {
  const parts = [];
  let remaining = text;
  let index = 0;

  const patterns = [
    {
      regex: /^\*\*(.+?)\*\*/,
      render: (match) => <strong key={`${keyPrefix}-${index++}`}>{match[1]}</strong>,
    },
    {
      regex: /^\[([^\]]+)\]\(([^)]+)\)/,
      render: (match) => {
        const label = match[1];
        const href = match[2];
        if (href.startsWith('/')) {
          return (
            <Link key={`${keyPrefix}-${index++}`} to={href} className="wiki-inline-link">
              {label}
            </Link>
          );
        }
        return (
          <a
            key={`${keyPrefix}-${index++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="wiki-inline-link"
          >
            {label}
          </a>
        );
      },
    },
    {
      regex: /^`([^`]+)`/,
      render: (match) => <code key={`${keyPrefix}-${index++}`} className="wiki-inline-code">{match[1]}</code>,
    },
  ];

  while (remaining.length) {
    let matched = false;
    for (const pattern of patterns) {
      const match = remaining.match(pattern.regex);
      if (match) {
        parts.push(pattern.render(match));
        remaining = remaining.slice(match[0].length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      const nextSpecial = remaining.search(/(\*\*|\[|`)/);
      if (nextSpecial === -1) {
        parts.push(remaining);
        break;
      }
      if (nextSpecial > 0) {
        parts.push(remaining.slice(0, nextSpecial));
        remaining = remaining.slice(nextSpecial);
      } else {
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      }
    }
  }

  return parts;
}

function parseTableRow(line) {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isTableDivider(line) {
  return /^\|?[\s:-]+\|[\s|:-]+\|?$/.test(line.trim());
}

function WikiMarkdown({ content }) {
  const blocks = useMemo(() => {
    const lines = content.replace(/\r\n/g, '\n').split('\n');
    const parsed = [];
    let index = 0;

    while (index < lines.length) {
      const line = lines[index];

      if (!line.trim()) {
        index += 1;
        continue;
      }

      if (line.startsWith('```')) {
        const language = line.slice(3).trim();
        index += 1;
        const codeLines = [];
        while (index < lines.length && !lines[index].startsWith('```')) {
          codeLines.push(lines[index]);
          index += 1;
        }
        index += 1;
        parsed.push({ type: 'code', language, code: codeLines.join('\n') });
        continue;
      }

      if (line.startsWith('### ')) {
        const text = line.slice(4);
        parsed.push({ type: 'h3', text, id: slugifyHeading(text) });
        index += 1;
        continue;
      }

      if (line.startsWith('## ')) {
        const text = line.slice(3);
        parsed.push({ type: 'h2', text, id: slugifyHeading(text) });
        index += 1;
        continue;
      }

      if (line.startsWith('# ')) {
        parsed.push({ type: 'h1', text: line.slice(2) });
        index += 1;
        continue;
      }

      if (line.startsWith('> ')) {
        const quoteLines = [];
        while (index < lines.length && lines[index].startsWith('> ')) {
          quoteLines.push(lines[index].slice(2));
          index += 1;
        }
        parsed.push({ type: 'blockquote', text: quoteLines.join(' ') });
        continue;
      }

      if (line.trim().startsWith('|')) {
        const tableLines = [];
        while (index < lines.length && lines[index].trim().startsWith('|')) {
          tableLines.push(lines[index]);
          index += 1;
        }
        const rows = tableLines.filter((row) => !isTableDivider(row)).map(parseTableRow);
        parsed.push({ type: 'table', rows });
        continue;
      }

      if (/^[-*]\s+/.test(line)) {
        const items = [];
        while (index < lines.length && /^[-*]\s+/.test(lines[index])) {
          items.push(lines[index].replace(/^[-*]\s+/, ''));
          index += 1;
        }
        parsed.push({ type: 'ul', items });
        continue;
      }

      if (/^\d+\.\s+/.test(line)) {
        const items = [];
        while (index < lines.length && /^\d+\.\s+/.test(lines[index])) {
          items.push(lines[index].replace(/^\d+\.\s+/, ''));
          index += 1;
        }
        parsed.push({ type: 'ol', items });
        continue;
      }

      const paragraphLines = [];
      while (
        index < lines.length
        && lines[index].trim()
        && !lines[index].startsWith('#')
        && !lines[index].startsWith('>')
        && !lines[index].trim().startsWith('|')
        && !/^[-*]\s+/.test(lines[index])
        && !/^\d+\.\s+/.test(lines[index])
        && !lines[index].startsWith('```')
      ) {
        paragraphLines.push(lines[index]);
        index += 1;
      }
      parsed.push({ type: 'p', text: paragraphLines.join(' ') });
    }

    return parsed;
  }, [content]);

  return (
    <article className="wiki-markdown">
      {blocks.map((block, blockIndex) => {
        switch (block.type) {
          case 'h1':
            return <h1 key={blockIndex}>{renderInline(block.text, `h1-${blockIndex}`)}</h1>;
          case 'h2':
            return (
              <h2 key={blockIndex} id={block.id}>
                {renderInline(block.text, `h2-${blockIndex}`)}
              </h2>
            );
          case 'h3':
            return (
              <h3 key={blockIndex} id={block.id}>
                {renderInline(block.text, `h3-${blockIndex}`)}
              </h3>
            );
          case 'p':
            return <p key={blockIndex}>{renderInline(block.text, `p-${blockIndex}`)}</p>;
          case 'blockquote':
            return (
              <blockquote key={blockIndex} className="wiki-blockquote">
                {renderInline(block.text, `q-${blockIndex}`)}
              </blockquote>
            );
          case 'ul':
            return (
              <ul key={blockIndex}>
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{renderInline(item, `ul-${blockIndex}-${itemIndex}`)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={blockIndex}>
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{renderInline(item, `ol-${blockIndex}-${itemIndex}`)}</li>
                ))}
              </ol>
            );
          case 'table':
            return (
              <div key={blockIndex} className="wiki-table-wrap">
                <table className="wiki-table">
                  <thead>
                    <tr>
                      {block.rows[0]?.map((cell, cellIndex) => (
                        <th key={cellIndex}>{renderInline(cell, `th-${blockIndex}-${cellIndex}`)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.slice(1).map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{renderInline(cell, `td-${blockIndex}-${rowIndex}-${cellIndex}`)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'code':
            return (
              <pre key={blockIndex} className="wiki-code-block">
                <code>{block.code}</code>
              </pre>
            );
          default:
            return null;
        }
      })}
    </article>
  );
}

export default WikiMarkdown;
